import {h} from 'preact';
import {ui} from '@playkit-js/kaltura-player-js';
import {debounce} from '@playkit-js/common/dist/utils-common/utils';
import {AAD_CUE_POINT_METADATA_TYPE_VALUE, AADCuePointMetadata, Parser} from './services/parse-webvtt-service';
import {CuePoint, TimedMetadataEvent} from '@playkit-js/common';
import {PluginAdvancedAudioDescriptionConfig} from './types/plugin-advanced-audio-description-config';
import {AadEvents, AadEventTriggers} from './types/events';
import {AttachmentAssetService} from './services/attachment-asset-service';
import {CaptionAssetService} from './services/caption-asset-service';
import AadOverlay from './components/aad-overlay';
import {getVoiceByLanguage} from './services/voices';

interface FakeEvent {
  payload?: {
    checked?: boolean;
    mute?: boolean;
  };
}

const {reducers, ReservedPresetNames} = ui;
export const pluginName = 'pluginAdvancedAudioDescription';

export class PluginAdvancedAudioDescription extends KalturaPlayer.core.BasePlugin {
  private readonly attachmentAssetService: AttachmentAssetService;
  private readonly captionAssetService: CaptionAssetService;
  private readonly synth: SpeechSynthesis;
  private _removeActiveOverlay: null | Function = null;
  private _onReplay: (trigger: AadEventTriggers) => void = () => {};
  private _lastAadCueStartTime: number = -1;
  private _resolveEadDataPromise = () => {};
  private _eadDataPromise: Promise<void>;
  private synthPause: boolean = false; //need for chrome as synth not working correctly
  private _timelineCuepoints: Array<{id: string}> = [];

  static readonly defaultConfig: PluginAdvancedAudioDescriptionConfig = {
    fullScreenOverlay: false,
    autoEnable: false,
    showTimelineMarkers: false
  };

  constructor(name: string, player: KalturaPlayerTypes.Player, config: PluginAdvancedAudioDescriptionConfig) {
    super(name, player, config);

    this.attachmentAssetService = new AttachmentAssetService(this.player, this.logger);
    this.captionAssetService = new CaptionAssetService(this.player, this.logger);
    this._eadDataPromise = this._makeEadDataPromise();

    this.synth = window.speechSynthesis;
    if (!this.synth) {
      this.logger.error('AAD Plugin: SpeechSynthesis is not supported');
    } else {
      this._init();
    }
  }

  get ready() {
    return this._eadDataPromise;
  }

  get isAADSettingOn() {
    const {audioDescription} = this.player.ui.store.getState();

    // TODO use ui enum when it is exported
    return audioDescription && audioDescription.isEnabled && audioDescription.selectedType === 2;
  }

  public static isValid(): boolean {
    return true;
  }

  loadMedia(): void {
    // this plugin is not supported for live entries
    if (this.player.isLive()) {
      this._resolveEadDataPromise();
      return;
    }
    this.run(this.player.sources.id)
      .catch((e: any) => {
        this.logger.error(`AAD Plugin: AAD captions haven't loaded, message: ${e.message}`);
      })
      .finally(() => {
        this._resolveEadDataPromise();
      });
  }
  private _makeEadDataPromise = () => {
    return new Promise<void>(res => {
      this._resolveEadDataPromise = res;
    });
  };

  private _init = () => {
    // listen when play/pause event happens
    this.eventManager.listen(this.player, this.player.Event.MUTE_CHANGE, this._onMute);

    // fired when the AAD Gear setting is On/Off
    this.eventManager.listen(this.player, this.player.Event.UI.USER_CLICKED_ADVANCED_AUDIO_DESCRIPTION, event => {
      this._stopSpeaking();
      const language = (this.player.getActiveTracks() as any)?.[this.player.Track.AUDIO]?.language;
      if (this.isAADSettingOn) {
        this.dispatchEvent(AadEvents.ON, {language, settings: event.payload?.settings});
        this.speakCurrentlyInRangeCuePoint();
      } else {
        this.dispatchEvent(AadEvents.OFF, {language, settings: event.payload?.settings});
        this._removeOverlay();
      }
    });

    // reset _lastAadCueStartTime when seeking back
    this.eventManager.listen(this.player, this.player.Event.SEEKING, () => {
      if (this.isAADSettingOn && this.player.currentTime <= this._lastAadCueStartTime) {
        this._lastAadCueStartTime = -1;
        this.speakCurrentlyInRangeCuePoint();
      }
    });

    // stop speaking when page is closed
    this.eventManager.listen(window, 'beforeunload', this._handlePageUnload);
  };

  private speakCurrentlyInRangeCuePoint() {
    const cuePointsInRange = this._filterAadCuePoints(this.player.cuePointManager.getActiveCuePoints());
    if (cuePointsInRange.length === 0) {
      return;
    }
    this.speakCuePoints(cuePointsInRange);
  }

  private _filterAadCuePoints(cuePoints: Array<CuePoint<any>>): Array<CuePoint<AADCuePointMetadata>> {
    return cuePoints.filter((cuePoint: CuePoint<any>) => cuePoint.metadata.type === AAD_CUE_POINT_METADATA_TYPE_VALUE);
  }

  private _onMute = (event: FakeEvent) => {
    if (event.payload?.mute === true && this.synth.speaking) {
      this.synth.cancel();
    }
  };

  private _stopSpeaking() {
    if (this.synth.speaking) {
      this.synth.cancel();
      this.player.play();
    }
  }

  private async _loadVTT(entryId: string): Promise<Array<CuePoint<AADCuePointMetadata>>> {
    const textFile = await this.attachmentAssetService.fetchVttFile(entryId);
    if (!textFile) {
      return [];
    }
    const parsedVtt = Parser.parse(textFile);
    return parsedVtt.cues || [];
  }

  async run(entryId: string) {
    let captions: Array<CuePoint<AADCuePointMetadata>> = [];
    let languages: string[] = [];

    try {
      const vttAssetCaptions = await this._loadVTT(entryId);
      if (Array.isArray(vttAssetCaptions) && vttAssetCaptions.length) {
        captions = vttAssetCaptions.map(c => ({...c, language: 'en'}));
        languages = ['en'];
        this.logger.debug(`AAD Plugin: vvt file asset loaded`);
      }
    } catch (e: any) {
      this.logger.debug(`AAD Plugin: vvt file asset not loaded, try use caption asset`, e);
    }

    try {
      let captionAssetCaptions = await this.captionAssetService.fetchAllAadCaptions(entryId);
      if (Array.isArray(captionAssetCaptions) && captionAssetCaptions.length) {
        captions = captionAssetCaptions;
        languages = Array.from(new Set(captions.map(caption => caption.metadata.language || '')));
        this.logger.debug(`AAD Plugin: AAD caption asset loaded`);
      }
    } catch (e: any) {
      this.logger.debug(`AAD Plugin: AAD caption asset not loaded, try use vtt file asset`, e);
    }

    if (captions.length) {
      // Add player cue points to be invoked using TIMED_METADATA_CHANGE listener
      this.player.cuePointManager.addCuePoints(captions);
      this.logger.debug('AAD Plugin: AAD captions loaded successfully, turn on AAD plugin');
      this.eventManager.listen(this.player, this.player.Event.TIMED_METADATA_CHANGE, this._onTimedMetadataChange);
      this.eventManager.listen(this.player, this.player.Event.TIMED_METADATA_ADDED, this._onTimedMetadataAdded);
    } else {
      this.logger.warn("AAD Plugin: Kaltura caption asset and vvt file doen't have AAD captions");
    }

    // TODO replace with imported type when ui type is updated
    // @ts-ignore
    this.player.ui.store.dispatch(window.KalturaPlayer.ui.reducers.audioDescription.actions.updateAdvancedAudioDescriptionLanguages(languages));
  }

  private _onTimedMetadataChange = ({payload}: TimedMetadataEvent) => {
    if (!this.isAADSettingOn) {
      return;
    }
    const AADCuePoints: Array<CuePoint<AADCuePointMetadata>> = this._filterAadCuePoints(payload.cues);
    this.speakCuePoints(AADCuePoints);
  };

  private _onTimedMetadataAdded = ({payload}: TimedMetadataEvent) => {
    if (!this.config.showTimelineMarkers){
      return;
    }
    const AADCuePoints: Array<CuePoint<AADCuePointMetadata>> = this._filterAadCuePoints(payload.cues);

    if (AADCuePoints.every(cuePoint => cuePoint.metadata.isExternal === true)) {
      this._addTimelineMarkers(AADCuePoints);
    }
  };

  private speakCuePoints(cuePoints: CuePoint<AADCuePointMetadata>[]): void {
    if (!cuePoints.length) {
      return;
    }
    const audioLanguage = (this.player.getActiveTracks() as any)?.[this.player.Track.AUDIO]?.language;
    const lastAadCue = cuePoints.find(c => c.metadata.language?.startsWith(audioLanguage)) || cuePoints[cuePoints.length - 1];
    const textToSpeech = lastAadCue.metadata.text;
    const aadCueStartTime = lastAadCue.startTime;
    const language = lastAadCue.metadata.language || 'en';

    if (
      !textToSpeech ||
      textToSpeech === '' ||
      // @ts-expect-error Property 'muted' does not exist on type 'Player'
      this.player.muted ||
      aadCueStartTime === this._lastAadCueStartTime
    ) {
      return;
    }
    // prevent speaking the same text again
    this._lastAadCueStartTime = aadCueStartTime;
    this.player.pause();
    this.dispatchEvent(AadEvents.PLAY);

    this._onReplay = (trigger: AadEventTriggers) => {
      this.dispatchEvent(AadEvents.REPLAY, {trigger});
      this.synth.cancel();
      this._speakText(textToSpeech, language);
    };

    this._setOverlay(textToSpeech);
    this._speakText(textToSpeech, language);
  }

  private _speakText(text: string, language: string) {
    const utterance: SpeechSynthesisUtterance = new SpeechSynthesisUtterance(text);
    utterance.rate = this.player.playbackRate;
    //@ts-expect-error - volume exists in kaltura player.
    utterance.volume = this.player.volume;

    const voice = getVoiceByLanguage(language);
    if (voice) {
      utterance.voice = voice;
    }

    utterance.onend = event => {
      this._removeOverlay();
      this.player.play();
    };

    utterance.onerror = event => {
      if (['canceled', 'interrupted'].includes(event.error)) {
        return;
      }
      this.logger.error(`Error at Speech: ${event.error}. (Hint: user interaction with the page is needed for privacy reasons).`);
    };

    this.synth.speak(utterance);
  }

  private _onSkip = (trigger: AadEventTriggers) => {
    this.dispatchEvent(AadEvents.SKIP, {trigger});
    this._removeOverlay();
    this.player.play();
  };

  private _onScroll = () => {
    this.dispatchEvent(AadEvents.SCROLL);
  };
  private _debouncedOnScroll = debounce(this._onScroll, 250);

  /**
   * pause / resume handler
   *
   * @param {AadEventTriggers} trigger - the trigger of the event, either button click or keyboard shortcut
   * @returns {boolean} - true if paused, false if resumed
   * @memberof pluginAdvancedAudioDescription
   */
  private _onPauseResume = (trigger: AadEventTriggers): boolean => {
    if (!this.synth.speaking) {
      return false;
    }
    if (this.synth.paused || this.synthPause) {
      this.synth.resume();
      this.synthPause = false;
      this.dispatchEvent(AadEvents.EAD_resume, {trigger});
      return false;
    }
    this.synth.pause();
    this.synthPause = true;
    this.dispatchEvent(AadEvents.EAD_pause, {trigger});
    return true;
  };

  private _setOverlay = (captionContent: string) => {
    this._removeOverlay();
    this._removeActiveOverlay = this.player.ui.addComponent({
      label: 'aad-overlay',
      area: 'GuiArea',
      presets: [ReservedPresetNames.Playback],
      get: () => (
        <AadOverlay
          captionContent={captionContent}
          onReplay={this._onReplay}
          onSkip={this._onSkip}
          onPauseResume={this._onPauseResume}
          onScroll={this._debouncedOnScroll}
          fullScreenOverlay={Boolean(this.config.fullScreenOverlay)}
        />
      )
    });
  };

  private _removeOverlay = () => {
    if (this.synth.speaking) {
      this.synth.cancel();
    }
    if (this._removeActiveOverlay) {
      this._removeActiveOverlay();
      this._removeActiveOverlay = null;
      this.synthPause = false;
    }
  };

  private _handlePageUnload = () => {
    if (this.synth.speaking) {
      this.synth.cancel();
    }
  };

  private _addCuePoint(startTime: any, endTime: any, type: string, id: number | string, text: string) {
    this.timelineManager.addKalturaCuePoint(startTime, type, id, text, {
        onClick: ({cuePoint}: {cuePoint: {id: string}}) => {}, endTime
    });
  }

  private _addTimelineMarkers(captions: Array<CuePoint<AADCuePointMetadata>>) {
    if (!this.timelineManager) {
      return;
    }
    this.timelineManager.resetChapters();
    this._timelineCuepoints = [];
    let emptySegmentCounter = 1;
    for (let i = 0; i <= captions.length; ++i) {
      if (i === captions.length) {
        this._addCuePoint(captions[captions.length-1].endTime, this.player.duration, 'Chapter', `empty-segment-${emptySegmentCounter}`, '');
        emptySegmentCounter++;
      } else {
        if (i === 0 && captions[0].startTime > 0) {
          this._addCuePoint(0, captions[0].startTime, 'Chapter', 0, '');
        }
        const {metadata, startTime, endTime} = captions[i];
        const text = metadata.text;
        this._addCuePoint(startTime, endTime, 'ADChapter', i+1, '');
        this._timelineCuepoints.push({id: captions[i].id});
      }
    }
  }

  public get timelineManager(): any {
    return this.player.getService('timeline');
  }

  reset() {
    this._lastAadCueStartTime = -1;
    this._removeOverlay();
    this._eadDataPromise = this._makeEadDataPromise();
    this._timelineCuepoints = [];
  }

  destroy() {
    this.attachmentAssetService.destroy();
    this.eventManager.removeAll();
  }
}
